import { BaseBlok, BlokToComponentProps, blokToComponent, CookieService, useTranslation } from '@repo/roche-xx-shared';
import {
  ContainerColor,
  RocheUIButton,
  RocheUIButtonAppearance,
  RocheUIButtonType,
  RocheUIDialog,
  RocheUIDialogPosition,
  RocheUIGrid,
  RocheUIHeadline,
  RocheUISelect,
} from '@rocheglobal/roche-ui';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dialogQueue } from '../../helpers/dialog-queue';
import { getComponent } from '../../helpers/get-component';
import { preventDialogScroll } from '../../helpers/prevent-dialog-scroll';
import { isRichtextEmpty } from '../../helpers/roche-rich-text-is-empty';

type RocheOnLoadNoticeProps = BlokToComponentProps<{
  _uid?: string;
  confirm_button_text?: string;
  cancel_button_text?: string;
  dont_show_message_text?: string;
  hide_notice_checkbox: boolean;
  redirect_link?: string;
  slotted: BaseBlok[];
}>;

const COOKIE_VALUE = 'accepted';

export const RocheOnLoadNotice: FC<RocheOnLoadNoticeProps> = ({ blok }) => {
  const headlineFallbackTranslation = useTranslation('please be aware that you are leaving this website');
  const continueLabel = blok.confirm_button_text;
  const cancelLabel = blok.cancel_button_text;
  const dontShowMessageLabel = blok.dont_show_message_text;
  const continueTranslationFallback = useTranslation('continue');
  const cancelTranslationFallback = useTranslation('cancel');
  const selectTranslationFallback = useTranslation('do not show this message again');

  const continueTranslation = continueLabel || continueTranslationFallback;
  const cancelTranslation = cancelLabel || cancelTranslationFallback;
  const selectTranslation = dontShowMessageLabel || selectTranslationFallback;

  const [checkedSelect, setCheckedSelect] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const dialogRef = useRef<HTMLDialogElement>(null);

  const cookieId =
    blok._uid ||
    encodeURI(typeof window !== 'undefined' ? window.location.href.replace(window.location.origin, '') : '').replace(
      /[^a-zA-Z0-9]/g,
      '',
    );

  const isOneTrustBannerVisible = (): boolean => {
    const banner = document.getElementById('onetrust-banner-sdk');
    return !!banner && banner.style.opacity !== '0';
  };

  const handleCancel = useCallback(() => {
    if (isOneTrustBannerVisible()) return;
    dialogRef.current?.close();
    setShouldShow(false);
  }, []);

  const handleContinue = useCallback(() => {
    if (CookieService.getConsent().necessary && checkedSelect) {
      CookieService.setCookie(`onLoadNotice${cookieId}`, COOKIE_VALUE, { expires: 182 });
    }
    dialogRef.current?.close();
    setShouldShow(false);
  }, [checkedSelect, cookieId]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (blok.hide_notice_checkbox) {
      CookieService.removeCookie(`onLoadNotice${cookieId}`);
    }

    if (CookieService.getCookie(`onLoadNotice${cookieId}`) !== COOKIE_VALUE) {
      setShouldShow(true);
    }
  }, [blok.hide_notice_checkbox, cookieId]);

  useEffect(() => {
    if (shouldShow && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.show();
      dialogQueue.block(dialogRef.current);
      preventDialogScroll(dialogRef.current);
    }
  }, [shouldShow]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const handleClose = () => {
      setShouldShow(false);
      dialogQueue.unblock();
    };

    let elementsToInert: HTMLElement[] = [];

    if (shouldShow && dialog) {
      elementsToInert = Array.from(document.body.children).filter(
        (child) => child !== dialog && child instanceof HTMLElement && child.tagName !== 'DIALOG',
      ) as HTMLElement[];

      elementsToInert.forEach((element) => {
        element.inert = true;
      });
    }

    dialog.addEventListener('close', handleClose);

    return () => {
      dialog.removeEventListener('close', handleClose);
      elementsToInert.forEach((element) => {
        element.inert = false;
      });
    };
  }, [shouldShow]);

  const children = blok.slotted?.map((item) => blokToComponent({ getComponent, blok: item }, 'roche-on-load-notice'));

  const hasContent =
    blok.slotted && Array.isArray(blok.slotted)
      ? (blok.slotted as BaseBlok[]).some((item) => !isRichtextEmpty(item))
      : false;

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <RocheUIDialog
      ref={dialogRef}
      variant={RocheUIDialogPosition.TOP}
      isOpen={shouldShow}
      onCancel={handleCancel}
      enableEscapeKey
    >
      <RocheUIGrid layout='10' backgroundColor={ContainerColor.TRANSPARENT} noPadding>
        <RocheUIDialog.Panel slot='full'>
          <RocheUIDialog.Content noPaddingBottom>
            {hasContent ? children : <RocheUIHeadline level='2' headline={headlineFallbackTranslation} />}
            {!blok.hide_notice_checkbox && (
              <RocheUISelect
                label=''
                options={[{ label: selectTranslation, value: 'hide-on-load-notice' }]}
                isMultiSelect
                selected={checkedSelect ? ['hide-on-load-notice'] : []}
                onSelect={() => setCheckedSelect(!checkedSelect)}
                showError={false}
                required={false}
              />
            )}
          </RocheUIDialog.Content>
          <RocheUIDialog.Footer noPaddingTop>
            <RocheUIButton
              appearance={RocheUIButtonAppearance.SECONDARY}
              text={cancelTranslation}
              onClick={handleCancel}
              type={RocheUIButtonType.BUTTON}
              href={blok.redirect_link}
            />
            <RocheUIButton text={continueTranslation} type={RocheUIButtonType.BUTTON} onClick={handleContinue} />
          </RocheUIDialog.Footer>
        </RocheUIDialog.Panel>
      </RocheUIGrid>
    </RocheUIDialog>,
    document.body,
  );
};
